import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";

import KImage from './KImage';
import Config from './ClientConfig';
import Client from './Client';
import swal from 'sweetalert';
import noImage from './../images/noImage.png';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

// Memorized image cell (same pattern as Classes)
const MemoKImage = React.memo(function MemoKImage({ edit, image, imageText, saveBack, width = 128, height = 128 }) {
  const imgObj = { data: image || noImage, width, height };
  return (
    <KImage edit={edit} image={imgObj} imageText={imageText} saveBack={saveBack} />
  );
});

class AllergenRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      edit: !!props.edit,
      row: props.row || { name: '', image: null, image_off: null },
      nameValid: !!(props.row && props.row.name),
      somethingHasChanged: !!props.edit,
    };
    this.handleEditClick = this.handleEditClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.getImage = this.getImage.bind(this);
    this.getImageOff = this.getImageOff.bind(this);
    this.save = this.save.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      this.setState({
        row: this.props.row || { name: '', image: null, image_off: null },
      });
    }
  }

  handleEditClick() {
    this.setState({ edit: !this.state.edit });
  }

  onChange(e) {
    const { id, value } = e.target;
    const row = { ...(this.state.row || {}) };
    if (id === 'name') {
      row.name = value || '';
      this.setState({ nameValid: (value || '').trim().length > 0 });
    }
    this.setState({ row, somethingHasChanged: true });
  }

  getImage(newImage) {
    if (!newImage) return;
    const row = { ...(this.state.row || {}) };
    row.image = newImage;
    this.setState({ row, somethingHasChanged: true });
  }

  getImageOff(newImage) {
    if (!newImage) return;
    const row = { ...(this.state.row || {}) };
    row.image_off = newImage;
    this.setState({ row, somethingHasChanged: true });
  }

  save() {
    const { row, nameValid, somethingHasChanged } = this.state;
    if (!somethingHasChanged || !nameValid) {
      return swal('Error', 'Please fill in a valid name before saving.', 'error');
    }

    const payload = {
      name: row.name || '',
      image: row.image || null,
      image_off: row.image_off || null,
    };
    if (row.iref) payload.iref = row.iref;

    const resp = this.state.client.pushData(Config.API_URL_ALLERGENS + "add/", payload);
    if (resp) {
      if (resp.status === 'OK') {
        const nRow = { ...row, ...resp };
        this.setState({ row: nRow, edit: false, somethingHasChanged: false });
        swal('Success', `Allergen saved (${nRow.iref})`, 'success', { buttons: false, timer: 1600 })
          .then(() => this.props.onSaved && this.props.onSaved());
      } else {
        swal('Error', resp.message || 'Save failed', 'error');
      }
    } else {
      swal('Error', 'No response from server', 'error');
    }
  }

  render() {
    const r = this.state.row || {};
    const canSave = this.state.edit && this.state.somethingHasChanged && this.state.nameValid;

    const imageName = `${r.name || 'allergen'}_on`;
    const imageOffName = `${r.name || 'allergen'}_off`;

    return (
      <StyledTableRow key={r.iref || 0}>
        <StyledTableCell align="left">{r.iref || ''}</StyledTableCell>
        <StyledTableCell>
          {this.state.edit ? (
            <TextField
              id="name"
              label="Name"
              variant="outlined"
              value={r.name || ''}
              onChange={this.onChange}
              onFocus={(e) => e.target.select()}
              inputProps={{ maxLength: '64' }}
              error={!this.state.nameValid}
              required
              sx={{ minWidth: 200 }}
              autoFocus={!!this.props.autoFocusKey}
            />
          ) : (
            r.name || ''
          )}
        </StyledTableCell>
        <StyledTableCell align="left">
          <MemoKImage edit={this.state.edit} image={r.image} imageText={imageName} saveBack={this.getImage} />
        </StyledTableCell>
        <StyledTableCell align="left">
          <MemoKImage edit={this.state.edit} image={r.image_off} imageText={imageOffName} saveBack={this.getImageOff} />
        </StyledTableCell>
        <StyledTableCell align="right">
          <IconButton onClick={this.handleEditClick} title="Edit">
            <EditIcon sx={{ color: this.state.edit ? '#8f241d' : '#e3e3e5' }} />
          </IconButton>
          {canSave && (
            <IconButton onClick={this.save} title="Save">
              <SaveIcon />
            </IconButton>
          )}
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

export default class Allergens extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      rows: null,
      addRow: false,
      maxAllergens: 15,
    };
    this.toggleAdd = this.toggleAdd.bind(this);
    this.refresh = this.refresh.bind(this);
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    const cfg = this.state.client.getData(Config.API_URL_CONFIG_SALESAPP, { key: "ALLERGENS_MAX" });
    const v = cfg?.SalesAppConfigs?.[0]?.value;
    if (v) this.setState({ maxAllergens: parseInt(v, 10) || 15 });
    this.refresh();
  }

  refresh() {
    const queryData = { orderBy: 'name' };
    const data = this.state.client.getData(Config.API_URL_ALLERGENS, queryData);
    // Expect { Allergens: [...] }
    const rows = data && Array.isArray(data.Allergens) ? data.Allergens : (Array.isArray(data) ? data : []);
    this.setState({ rows });
  }

  toggleAdd() {
    const addButton = this.state.addRow;
    this.setState({ addRow: !addButton }, () => {
      if (!addButton && this.tableRef.current) {
        // smooth scroll when opening the add row
        const el = this.tableRef.current;
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }
    });
  }

  render() {
    const rows = Array.isArray(this.state.rows) ? this.state.rows : [];
    let allergensCounter = rows.length;

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ maxHeight: '85vh', bgcolor: 'background.paper', borderRadius: 0, border: 0 }}
        ref={this.tableRef}
      >
        <Table stickyHeader sx={{ minWidth: 700 }} aria-label="Allergens table">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <IconButton
                    aria-label="Add Allergen"
                    title="Add Allergen"
                    onClick={() => {
                      if (allergensCounter  >= this.state.maxAllergens) {
                        swal(
                          "Limit reached.",
                          `You can add up to ${this.state.maxAllergens} allergens. Edit an existing one.`,
                          "warning"
                        );
                        return;
                      }
                      this.toggleAdd();
                    }}
                  >
                  <Avatar alt="Add Allergen" sx={{ width: 24, height: 24, color: '#273442' }}>
                    {this.state.addRow ? <FaMinusCircle /> : <FaPlusCircle />}
                  </Avatar>
                </IconButton>
              </StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell align="left">Image (On)</StyledTableCell>
              <StyledTableCell align="left">Image (Off)</StyledTableCell>
              <StyledTableCell align="right">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.addRow && (
              <AllergenRow
                key={0}
                edit
                onSaved={() => {
                  this.toggleAdd();
                  this.refresh();
                }}
                autoFocusKey
              />
            )}
            {rows.map((r) => (
              <AllergenRow
                key={r.iref}
                row={r}
                onSaved={this.refresh}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
}
